/**
 * What the report header's export actions should do for a report the current
 * user can READ but does not OWN (a company-shared report generated by a
 * colleague or a parent-organisation member).
 *
 * Jobs retain the report owner's namespace. An authorized colleague may ask
 * the server to build a PDF from the exact revision; the server rechecks site
 * access on enqueue, polling and download.
 * Existing artifacts of an exact revision, however, are downloadable by every
 * authorised member through the historical download route
 * (`/api/reports/:reportId/revisions/:revisionId/artifacts/:format`, which
 * re-verifies site access server-side).
 *
 * Existing exports are reused. Other missing formats retain their existing
 * author-only creation rule. This resolver never grants server access.
 */
import type { SiteRagReport } from '../../types/siteRag';
import { reportRevisionReferenceV1Schema } from '../../types/reportContractsV2';
import type { PrivateReportArtifactDownload } from '../../services/privateReportArtifactDownload';

export type SharedReportExportFormat = 'pdf' | 'docx' | 'pptx';

export type SharedReportExportAction =
  | { kind: 'build'; reportOwnerUid?: string }
  | { kind: 'download'; artifact: PrivateReportArtifactDownload }
  | { kind: 'unavailable'; reason: string };

export interface ResolveSharedReportExportActionInput {
  report: SiteRagReport;
  /** Path owner of the report when known (`_ownerUid` stamp or index doc). */
  reportOwnerUid?: string;
  currentUid: string | null;
  format: SharedReportExportFormat;
  pageSize?: 'A4' | 'Letter';
  filename: string;
}

const FORMAT_LABEL: Record<SharedReportExportFormat, string> = {
  pdf: 'PDF',
  docx: 'Word',
  pptx: 'PowerPoint',
};

export function resolveSharedReportExportAction(
  input: ResolveSharedReportExportActionInput,
): SharedReportExportAction {
  const ownerUid = input.reportOwnerUid ?? input.report._ownerUid;
  // An unstamped report is the requester's own; the owner builds/refreshes
  // artifacts through the job pipeline exactly as before.
  if (!ownerUid || ownerUid === input.currentUid) return { kind: 'build' };

  const label = FORMAT_LABEL[input.format];
  const authorOnly = `Only the report's author can build a new ${label} export of this report.`;

  const revision = reportRevisionReferenceV1Schema.safeParse(input.report.reportRevision);
  if (!revision.success) {
    return {
      kind: 'unavailable',
      reason: `${authorOnly} This report has no immutable revision yet, so there is nothing to download.`,
    };
  }

  const pageSize = input.pageSize ?? 'A4';
  const pointer = input.format === 'pdf'
    ? revision.data.pdfVariants?.[pageSize]?.artifact
      ?? (pageSize === 'A4' ? revision.data.artifacts.pdf : undefined)
    : revision.data.artifacts[input.format];

  if (!pointer || pointer.state !== 'ready') {
    if (input.format === 'pdf' && input.currentUid) {
      return { kind: 'build', reportOwnerUid: ownerUid };
    }
    return {
      kind: 'unavailable',
      reason: `${authorOnly} Ask them to export this revision; once it is built you can download it here.`,
    };
  }
  if (input.format === 'pdf' && !pointer.layoutFingerprint) {
    return {
      kind: 'unavailable',
      reason: `${authorOnly} The existing PDF export carries no layout identity, so it cannot be verified for download.`,
    };
  }

  return {
    kind: 'download',
    artifact: {
      reportId: input.report.id,
      reportRevisionId: revision.data.revisionId,
      reportOwnerUid: ownerUid,
      format: input.format,
      sha256: pointer.sha256,
      mediaType: pointer.mediaType,
      byteLength: pointer.byteLength,
      ...(input.format === 'pdf'
        ? { pageSize, layoutFingerprint: pointer.layoutFingerprint }
        : {}),
      filename: input.filename,
    },
  };
}
